import { useMemo } from 'react';
import { Box, HStack, Image, VStack } from 'native-base';
import moment from 'moment';
import { range } from 'lodash';

import { CurrencyText, Text } from '@pimm/base';
import { WidgetBox, WidgetBoxProps } from '@app/features/widgets/widget-box';
import { useSiteTime } from '@app/features/store-core';
import { useGetSalesAndLabor } from '@app/features/sales-tracker-v2/hooks/get-sales-and-labor.hook';
import { ProgressBar } from '@app/features/sales-tracker-v2/components/progress-bar';
import { VarianceBadge } from '@app/features/sales-tracker-v2/components/variance-badge';
import { getLaborVarianceColors, fmtHoursVar } from '@app/features/sales-tracker-v2/_helper';
import { LABOR_VARIANCE_THRESHOLDS } from '@app/features/sales-tracker-v2/consts';
import { stringToDateLocal } from '@app/utils/date-formatter';
import { useTranslation } from 'react-i18next';
import { useAppLocale } from '@pimm/common';

const MAX_LABOR_SCALE = 6;

type WidgetSalesLaborProps = WidgetBoxProps & {};

export const WidgetSalesLabor = ({ ...props }: WidgetSalesLaborProps) => {
  const siteTime = useSiteTime();
  const { t } = useTranslation();
  const { translate } = useAppLocale();
  const selectedDate = siteTime.toStartEndOfPart().startTime;
  const query = useGetSalesAndLabor({ siteId: siteTime.siteId, date: selectedDate });

  const syncInfo = query.data?.syncInfo;
  const isOnline = syncInfo?.status?.toLowerCase() === 'online';
  const displayForecastedSales = query.data?.salesLaborData?.displayForecastedSales ?? false;
  const hasLaborTracker = query.data?.salesLaborData?.displayLaborHours ?? false;

  // Aggregate into widget totals using 30-min granularity — intentionally different from the
  // full tracker which requires both 30-min slots in an hour to have data before counting.
  // The widget sums every confirmed 30-min block directly, giving managers a more current read.
  const { actual, paceForecast, fullDayForecast, laborCumVar } = useMemo(() => {
    const blocks = query.data?.salesLaborData?.blocks ?? [];
    const lastDataSyncUtc = syncInfo?.lastDataSyncUtc;
    const syncCutoffMs =
      lastDataSyncUtc && siteTime.utcOffsetInMinutes !== undefined
        ? new Date(lastDataSyncUtc).getTime() + siteTime.utcOffsetInMinutes * 60_000
        : undefined;

    // Build 48 × 30-min slots and sum confirmed blocks directly (no hourly aggregation)
    const thirtyMinSlots = range(48).map(i => {
      const time = moment(selectedDate).add(30 * i, 'minutes');
      const block = blocks.find(b => b.dayDateTime && stringToDateLocal(b.dayDateTime)?.getTime() === time.valueOf());
      const blockEndMs = block?.dayDateTime ? new Date(block.dayDateTime).getTime() + 30 * 60 * 1_000 : undefined;
      const hasActual = blockEndMs !== undefined && syncCutoffMs !== undefined ? blockEndMs <= syncCutoffMs : !!block;
      return {
        hasActual,
        salesActual: block?.salesActual ?? 0,
        salesForecast: block?.salesForecasted ?? 0,
        laborCumVar: block?.laborCumulativeGuideVariance ?? 0,
      };
    });

    const confirmedSlots = thirtyMinSlots.filter(s => s.hasActual);

    return {
      actual: Math.round(confirmedSlots.reduce((sum, s) => sum + s.salesActual, 0)),
      paceForecast: Math.round(confirmedSlots.reduce((sum, s) => sum + s.salesForecast, 0)),
      fullDayForecast: Math.round(thirtyMinSlots.reduce((sum, s) => sum + s.salesForecast, 0)),
      laborCumVar: confirmedSlots[confirmedSlots.length - 1]?.laborCumVar ?? 0,
    };
  }, [query.data, selectedDate, siteTime.utcOffsetInMinutes, syncInfo?.lastDataSyncUtc]);

  // Sales: abovePace drives section bg color; matches DaypartCard which compares actual vs elapsedForecast (pace)
  const abovePace = actual >= paceForecast;

  // Labor variance — reuse existing helper for badge colors
  const laborColors = getLaborVarianceColors('variance_cumulative', laborCumVar, false);
  const laborBadgeBg = laborColors.bg === 'white' ? 'gray.50' : laborColors.bg;
  const laborAbs = Math.abs(laborCumVar);
  const laborIsOver = laborCumVar > 0;
  const laborFillPct = Math.min((laborAbs / MAX_LABOR_SCALE) * 100, 100) / 2;
  const laborBarBg = laborAbs >= LABOR_VARIANCE_THRESHOLDS.thirtyMin.cumulative.red ? 'error.500' : 'gray.400';

  // Sync timestamp: "Today 5:05 PM" or "Mar 23 5:05 PM"
  const syncTimeText = useMemo(() => {
    if (!syncInfo?.lastDataSyncUtc) return '--';
    const syncLocal = moment.utc(syncInfo.lastDataSyncUtc).utcOffset(siteTime.utcOffsetInMinutes ?? 0);
    const siteNow = siteTime.today();
    const isToday =
      syncLocal.year() === siteNow.getFullYear() && syncLocal.month() === siteNow.getMonth() && syncLocal.date() === siteNow.getDate();
    const timeStr = syncLocal.format('h:mm A');
    return isToday ? `${t('widgets:today')} ${timeStr}` : syncLocal.format('MMM D ') + timeStr;
  }, [syncInfo?.lastDataSyncUtc, siteTime, t]);

  return (
    <WidgetBox isLoading={query.isLoading} hideTitle {...props}>
      {/* Widget header */}
      <HStack alignItems="center" mb={1.5} mt={-1}>
        <Text size="sm" fontWeight={700} color="gray.600" lineHeight="md" textTransform="uppercase" numberOfLines={1} ellipsizeMode="tail">
          {translate(props?.title, props?.titleTranslations)}
        </Text>
      </HStack>

      <VStack flex={1} justifyContent="space-between">
        <VStack space={1}>
          {/* ── Sales section ── */}
          <Box bgColor={displayForecastedSales ? (abovePace ? 'blueLight.50' : 'error.50') : undefined} rounded="md" px={2} py={1.5}>
            <HStack justifyContent="space-between" alignItems="center" mb={displayForecastedSales ? 1 : 0}>
              <Text size="md" fontWeight={700} color="gray.600">
                {t('widgets:sales')}
              </Text>
              <HStack alignItems="center" space={1}>
                <CurrencyText size="xl" fontWeight={800} color="gray.900" hideFlatZero value={actual} />
                {displayForecastedSales && actual > 0 && <VarianceBadge actual={actual} forecast={paceForecast} bg="transparent" />}
              </HStack>
            </HStack>

            {displayForecastedSales && (
              <>
                <HStack justifyContent="space-between" mb={1}>
                  <HStack space={1} alignItems="center">
                    <Text size="sm" color="gray.600">
                      {t('widgets:pace')}
                    </Text>
                    <CurrencyText size="sm" fontWeight={700} color="gray.600" hideFlatZero value={paceForecast} />
                  </HStack>
                  <HStack space={1} alignItems="center">
                    <Text size="sm" color="gray.600">
                      {t('widgets:goal')}
                    </Text>
                    <CurrencyText size="sm" fontWeight={700} color="gray.600" hideFlatZero value={fullDayForecast} />
                  </HStack>
                </HStack>

                <ProgressBar actual={actual} elapsedForecast={paceForecast} fullForecast={fullDayForecast} />
              </>
            )}
          </Box>

          {/* ── Labor section ── */}
          {hasLaborTracker && (
            <Box px={2} py={1.5}>
              <HStack justifyContent="space-between" alignItems="center" mb={1}>
                <Text size="md" fontWeight={700} color="gray.600">
                  {t('widgets:labor')}
                </Text>
                <Box bgColor={laborBadgeBg} rounded="sm" px="7px" py="2px">
                  <Text size="sm" fontWeight={700} color={laborColors.text}>
                    {t('widgets:hrs_variance', { value: fmtHoursVar(laborCumVar) })}
                  </Text>
                </Box>
              </HStack>

              {/* Bidirectional bar — center = guide, fill extends left (under) or right (over) */}
              <Box rounded="full" w="full" h="5px" overflow="hidden" bg="gray.200">
                <Box w="full" h="full" position="relative">
                  {laborFillPct > 0 && (
                    <Box
                      position="absolute"
                      top={0}
                      bottom={0}
                      bg={laborBarBg}
                      left={`${laborIsOver ? 50 : 50 - laborFillPct}%`}
                      w={`${laborFillPct}%`}
                    />
                  )}
                  {/* Guide notch */}
                  <Box position="absolute" left="50%" top={0} bottom={0} w="2px" bg="gray.400" style={{ transform: [{ translateX: -1 }] }} />
                </Box>
              </Box>

              <HStack justifyContent="space-between" mt={0.5}>
                <Text size="sm" color="gray.600">
                  {t('widgets:under')}
                </Text>
                <Text size="sm" fontWeight={600} color="gray.700">
                  {t('widgets:guide')}
                </Text>
                <Text size="sm" color="gray.600">
                  {t('widgets:over')}
                </Text>
              </HStack>
            </Box>
          )}
        </VStack>

        {/* ── Footer: vendor logo + sync status ── */}
        {!!syncInfo && (
          <HStack justifyContent="space-between" alignItems="center" pt={1.5} mt={0.5} borderTopWidth={1} borderTopColor="gray.200">
            {syncInfo.vendorInfo?.logoImgUrl ? (
              <Image
                source={{ uri: syncInfo.vendorInfo.logoImgUrl }}
                style={{ height: 18, width: 80 }}
                resizeMode="contain"
                alt={syncInfo.vendorInfo?.fullName ?? 'Vendor'}
              />
            ) : (
              <Text size="xs" fontWeight={600} color="gray.500" numberOfLines={1}>
                {syncInfo.vendorInfo?.fullName ?? ''}
              </Text>
            )}

            <HStack alignItems="center" space={1}>
              {!isOnline && (
                <Text size="xs" fontWeight={700} color="error.600">
                  {t('widgets:offline')}
                </Text>
              )}
              <Box w="7px" h="7px" rounded="full" bgColor={isOnline ? 'success.500' : 'error.500'} />
              <Text size="xs" fontWeight={500} color="gray.600">
                {syncTimeText}
              </Text>
            </HStack>
          </HStack>
        )}
      </VStack>
    </WidgetBox>
  );
};
